import { cn } from '@/lib/utils';
import { JOB_BILLING_STATE_LABELS } from '@jobos/shared';
import type { JobBillingState } from '@jobos/shared';

/**
 * A job's derived billing state (spec §4).
 *
 * Replaces InvoiceStatusBadge, which read `jobs.invoice_status` directly and
 * hardcoded a 30-day overdue rule. Both are now wrong: the state is derived
 * from the payment ledger server-side, and the overdue threshold is
 * configurable per client. This component renders the server's verdict and
 * derives nothing.
 *
 * The columns it replaced stay in the database (NFR-4); only the component goes.
 */

const STATE_STYLES: Record<JobBillingState, string> = {
  not_billed: 'bg-gray-100 text-gray-700',
  drafted: 'bg-slate-100 text-slate-700',
  partly_billed: 'bg-blue-100 text-blue-800',
  sent: 'bg-yellow-100 text-yellow-800',
  partly_paid: 'bg-amber-100 text-amber-800',
  settled: 'bg-green-100 text-green-800',
};

export interface JobBillingStateBadgeProps {
  /** Undefined while the row's response has not arrived — renders nothing. */
  state: JobBillingState | undefined | null;
  className?: string;
}

export function JobBillingStateBadge({ state, className }: JobBillingStateBadgeProps) {
  if (!state) return null;

  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium',
        STATE_STYLES[state],
        className,
      )}
    >
      {JOB_BILLING_STATE_LABELS[state]}
    </span>
  );
}
